<script setup lang="ts">
import StripeProviderPayment from "~/components/payments/providers/StripeProviderPayment.vue"
import CardSelection from "~/components/payments/CardSelection.vue";

const {t} = useI18n();
const showModal = ref(false)
const providerComponents = {
  stripe: StripeProviderPayment
}
const provider = ref(null)
const showCards = ref(false)
const providerComponent = computed(() => providerComponents[provider.value] ?? null)
const paymentProviderRef = ref()
const paymentStore = usePaymentStore()
const isProcessing = ref(false)
const showSkeleton = ref(false)
const cards = ref([])
const selectedPaymentMethodId = ref(null)

const userAvatar = ref(null);

watch(
  () => paymentStore.currentProduct,
  async (value) => {
    if (value) {
      const userIdentifier =
        value.type === "subscribe"
          ? toRaw(value.author)?.username ?? null
          : value.username;
      showModal.value = true;
      await fetchPaymentMethods();
      try {
        const response = await $larafetch(`/users/${userIdentifier}`, {
          method: 'GET',
        });
        userAvatar.value = response.avatar;
      } catch (error) {
        console.error("Błąd podczas pobierania danych użytkownika:", error);
      }

    }
  }
);


const currentPayment = ref({
  intent: null,
  return_url: null,
  payment_id: null
})


async function fetchPaymentMethods() {
  try {
    const response = await $larafetch('/payment-methods', {
      method: 'GET',
    });

    cards.value = response.data
    if (cards.value.length < 1) {
      provider.value = 'stripe'
      await providerInit();

    } else {
      showCards.value = true;
    }

  } catch (error) {
    console.error('Wystąpił błąd:', error);
  }
}


async function providerInit() {
  isProcessing.value = true;
  showSkeleton.value = true;
  const response = await $larafetch(`${paymentStore.currentInitPaymentRequest.url}`, {
    method: 'post',
    body: {
      ...paymentStore.currentInitPaymentRequest.body,
      remember: saveCard.value
    }
  })
  currentPayment.value = {
    intent: response.intent_secret,
    return_url: response.return_url,
    payment_id: response.payment_id
  }
  isProcessing.value = false;
  selectedPaymentMethodId.value = null
}


const isOpen = ref(false)

const closeDetails = (event: Event) => {
  const detailsElement = document.querySelector('#details-element');
  if (detailsElement && !detailsElement.contains(event.target as Node)) {
    isOpen.value = false;
  }
};


//REMEMBER CARD

const saveCard = ref(true);

function onChecked(id, value) {
  if (id === 'save_card') {
    saveCard.value = value;
    if (currentPayment.value.payment_id) {
      $larafetch(`/payment/${currentPayment.value.payment_id}`, {
        method: 'put',
        body: {
          remember: value
        }
      })
    }
  }
}

const selectedPlan = ref('monthly');

async function pay() {
  isProcessing.value = true
  if (selectedPaymentMethodId.value) {
    const interval = selectedPlan.value === 'monthly' ? 'month' : 'year';
    const data = await $larafetch(`${paymentStore.currentInitPaymentRequest.url}/${selectedPaymentMethodId.value}`, {
      method: 'post',
      body: {
        ...paymentStore.currentInitPaymentRequest.body,
        interval: interval,
        remember: saveCard.value
      }
    })

    if (data.success) {
      window.location = data.return_url
    }
  } else {
    await paymentProviderRef.value.$.exposed.pay();

  }
}

function onModalClose() {
  showModal.value = false
  nextTick(() => {
    paymentStore.clear();
    isProcessing.value = false
    provider.value = null
    selectedPaymentMethodId.value = null
  })
}

function handleSelectedCard(newCard) {
  provider.value = null
  showSkeleton.value = false;
  selectedPaymentMethodId.value = newCard
}

function handleProviderInit() {
  provider.value = 'stripe'
  providerInit()
}
</script>

<template>
  <Modal v-if="showModal && paymentStore.currentProduct" :model-value="true" @update:model-value="onModalClose">
    <template #dialog-content>
      <DialogContent class="overflow-y-auto max-h-[95vh] outline-none border-[0.5px] border-gray-50">
        <DialogHeader>
          <button @click="onModalClose" class="w-auto text-left font-normal text-[17px]"
                  variant="ghost">
            {{ t('buttons.cancel') }}
          </button>
          <h2 class="text-center">{{ t('payment.summary.title') }}</h2>
        </DialogHeader>
        <ScrollArea class="w-full">
          <NuxtLazyHydrate when-visible>
            <div class="pt-0 flex flex-col w-full">
              <PaymentsPaymentSummary
                v-if="paymentStore.currentProduct.type === 'one-time-payment'"
                :avatar="userAvatar"
                :author-username="paymentStore.currentProduct.username"
                :total="Number(paymentStore.currentProduct.price.toFixed(2))"
                :currency_code="paymentStore.currentProduct.currency_code"
              />
              <PaymentsPaymentSubscribeSummary
                v-else-if="paymentStore.currentProduct.type === 'subscribe'"
                :avatar="userAvatar"
                :author="paymentStore.currentProduct.author"
                v-model:selectedPlan="selectedPlan"
              />
              <div v-if="showCards" class="p-4 md:p-6 md:pb-4">
                <CardSelection :cards="cards" :addNewCard="true" @addNewCardSelected="handleProviderInit"
                               @update:selectedCard="handleSelectedCard"/>
              </div>
              <div v-if="providerComponent" class="p-4 md:p-6">
                <component :is="providerComponent" :current-payment="currentPayment"
                           ref="paymentProviderRef" @error="isProcessing = false"/>
              </div>
              <div
                v-if="(!selectedPaymentMethodId  && !providerComponent) || (showSkeleton && !providerComponent)"
                class="p-4 md:p-6">
                <Skeleton class="w-full h-14 rounded-xl"/>
              </div>

              <div class="px-4 md:px-6 mt-4 sticky bottom-0 bg-background">
                <div class="flex items-center space-x-2" v-if="provider">
                  <Checkbox :checked="saveCard"
                            @update:checked="v => onChecked('save_card', v)" id="terms"/>
                  <label for="terms" class="cursor-pointer font-medium">
                    {{ t('payment.save_card') }}
                  </label>
                </div>
                <Button :disabled="(!provider && !selectedPaymentMethodId) ||  isProcessing"
                        class="h-10 my-4 md:mb-6 disabled:opacity-30" :pending="isProcessing"
                        @click="pay"> {{ t('buttons.pay_now') }}
                </Button>
              </div>
            </div>
          </NuxtLazyHydrate>
        </ScrollArea>
      </DialogContent>
    </template>
    <template #drawer-content>
      <DrawerContent class="min-h-auto h-auto max-h-dvh overflow-hidden">
        <DrawerHeader>
          {{ t('payment.summary.title') }}
        </DrawerHeader>
        <NuxtLazyHydrate when-visible>
          <div class="pt-0 pb-1 flex flex-col w-full overflow-y-scroll">
            <PaymentsPaymentSummary
              v-if="paymentStore.currentProduct.type === 'one-time-payment'"
              :avatar="userAvatar"
              :author-username="paymentStore.currentProduct.username"
              :total="Number(paymentStore.currentProduct.price.toFixed(2))"
              :currency_code="paymentStore.currentProduct.currency_code"
            />
            <PaymentsPaymentSubscribeSummary
              v-else-if="paymentStore.currentProduct.type === 'subscribe'"
              :avatar="userAvatar"
              :author="paymentStore.currentProduct.author"
              v-model:selectedPlan="selectedPlan"
            />
            <div v-if="showCards" class="p-4 md:p-6 md:pb-4">
              <CardSelection :cards="cards" :addNewCard="true" @addNewCardSelected="handleProviderInit"
                             @update:selectedCard="handleSelectedCard"/>
            </div>
            <div v-if="providerComponent" class="p-4 md:p-6">
              <component :is="providerComponent" :current-payment="currentPayment"
                         ref="paymentProviderRef" @error="isProcessing = false"/>
            </div>
            <div
              v-if="(!selectedPaymentMethodId  && !providerComponent) || (showSkeleton && !providerComponent)"
              class="p-4 md:p-6">
              <Skeleton class="w-full h-14 rounded-xl"/>
            </div>
            <div class="px-4 md:px-6 mt-4 sticky bottom-0 bg-background">
              <div class="flex items-center space-x-2" v-if="provider">
                <Checkbox :checked="saveCard"
                          @update:checked="v => onChecked('save_card', v)" id="terms"/>
                <label
                  for="terms"
                  class="cursor-pointer font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {{ t('payment.save_card') }}
                </label>
              </div>
              <Button :disabled="(!provider && !selectedPaymentMethodId) ||  isProcessing"
                      class="h-10 my-4 md:mb-6 disabled:opacity-30" :pending="isProcessing"
                      @click="pay">{{ t('buttons.pay_now') }}
              </Button>
            </div>
          </div>
        </NuxtLazyHydrate>
      </DrawerContent>
    </template>
  </Modal>
</template>
<style scoped>
/* Dodanie płynnego przejścia fade */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.8s ease, transform 0.8s ease; /* Dodanie transform dla płynności */
}

/* Ustawienie początkowych i końcowych stanów przejścia */
.fade-enter, .fade-leave-to /* .fade-leave-active w wersjach Vue <2.1.8 */
{
  opacity: 0;
  transform: translateY(10px); /* Przesunięcie w pionie dla płynności */
}

/* Normalizacja pozycji */
.fade-leave, .fade-enter-to {
  opacity: 1;
  transform: translateY(0);
}
</style>